import { Radio as RadioPrimitive } from "@base-ui/react/radio";
import { RadioGroup as RadioGroupPrimitive } from "@base-ui/react/radio-group";
import { cva } from "class-variance-authority";
import { createContext, use, useId } from "react";
import { cn } from "@/lib/utils";
import {
  RadioDotIcon,
  RadioOutlineIcon,
} from "../radio-indicator/radio-indicator";

// propagates group-level error, disabled, and size to each Radio
type RadioGroupContextValue = {
  error?: boolean;
  disabled?: boolean;
  size?: "sm" | "md";
};
const RadioGroupContext = createContext<RadioGroupContextValue>({});

const radioControlVariants = cva(
  [
    "relative",
    "shrink-0",
    "inline-flex",
    "items-center",
    "justify-center",
    "rounded-full",
    "group",
    // disabled
    "data-[disabled]:pointer-events-none",
    // focus
    "focus-visible:outline-none",
    "focus-visible:ring-[1.5px]",
    "focus-visible:ring-radio-focus-ring",
    "cursor-[inherit]",
    // coarse touch target
    "[@media(pointer:coarse)]:before:content-['']",
    "[@media(pointer:coarse)]:before:absolute",
  ],
  {
    variants: {
      size: {
        sm: [
          "w-(--icon-sm)",
          "h-(--icon-sm)",
          "[@media(pointer:coarse)]:before:inset-[-12px]",
        ],
        md: [
          "w-(--icon-md)",
          "h-(--icon-md)",
          "[@media(pointer:coarse)]:before:inset-[-10px]",
        ],
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

const radioOutlineContainerVariants = cva([
  "absolute",
  "inset-0",
  "rounded-full",
  "flex",
  "items-center",
  "justify-center",
  "[[data-checked]_&]:hidden",
  "group-hover:bg-radio-outline-hover-background",
  "group-active:bg-radio-outline-pressed-background",
]);

const radioOutlineIconVariants = cva(
  [
    "flex",
    "items-center",
    "justify-center",
    "text-radio-outline-default-border",
    "group-hover:text-radio-outline-hover-border",
    "group-active:text-radio-outline-pressed-border",
    "[[data-disabled]_&]:text-radio-outline-disabled-border",
    "[[data-error]_&]:text-error",
  ],
  {
    variants: {
      size: {
        sm: ["w-[calc(var(--icon-sm)*0.8)]", "h-[calc(var(--icon-sm)*0.8)]"],
        md: ["w-[calc(var(--icon-md)*0.8)]", "h-[calc(var(--icon-md)*0.8)]"],
      },
    },
    defaultVariants: { size: "sm" },
  },
);

const radioFilledIndicatorVariants = cva(
  [
    "flex",
    "items-center",
    "justify-center",
    "text-radio-filled-default-background",
    "group-hover:text-radio-filled-hover-background",
    "group-active:text-radio-filled-pressed-background",
    "[[data-disabled]_&]:text-radio-filled-disabled-background",
    "[[data-error]_&]:text-error",
  ],
  {
    variants: {
      size: {
        sm: ["w-[calc(var(--icon-sm)*0.8)]", "h-[calc(var(--icon-sm)*0.8)]"],
        md: ["w-[calc(var(--icon-md)*0.8)]", "h-[calc(var(--icon-md)*0.8)]"],
      },
    },
    defaultVariants: { size: "sm" },
  },
);

const labelTextStyles = cva([
  "text-text-default",
  "font-(family-name:--font-family-label)",
  "font-(number:--font-weight-medium)",
  "text-(length:--font-size-label-large)",
  "leading-(--font-line-height-20)",
  "tracking-(--font-letter-spacing-default)",
]);

const descriptionStyles = cva([
  "text-text-subtext",
  "font-(family-name:--font-family-body)",
  "font-(number:--font-weight-regular)",
  "text-(length:--font-size-body-medium)",
  "leading-(--font-line-height-20)",
  "tracking-(--font-letter-spacing-default)",
]);

const radioItemVariants = cva(["flex", "flex-col", "gap-(--spacing-8)"], {
  variants: {
    disabled: {
      true: ["cursor-not-allowed"],
      false: ["cursor-pointer"],
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

const descriptionIndentVariants = cva([], {
  variants: {
    size: {
      sm: ["pl-[calc(var(--icon-sm)+var(--spacing-8))]"],
      md: ["pl-[calc(var(--icon-md)+var(--spacing-8))]"],
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

// ============================================
// RadioGroup
// ============================================

export type RadioGroupProps = Omit<
  RadioGroupPrimitive.Props<string>,
  "className" | "aria-label" | "aria-labelledby" | "aria-invalid"
> & {
  error?: boolean;
  size?: "sm" | "md";
} & (
    | { label: string; "aria-label"?: never; "aria-labelledby"?: never }
    | { label?: never; "aria-label": string; "aria-labelledby"?: never }
    | { label?: never; "aria-label"?: never; "aria-labelledby": string }
  );

/**
 * Wraps two or more Radio items and manages mutual exclusion.
 * Exactly one item can be selected at a time.
 *
 * @example
 * <RadioGroup label="Options" value={value} onValueChange={setValue}>
 *   <Radio value="a" label="Option A" />
 *   <Radio value="b" label="Option B" />
 * </RadioGroup>
 */
function RadioGroupComponent({
  label,
  error,
  disabled,
  size = "sm",
  children,
  ...props
}: RadioGroupProps) {
  const id = useId();

  return (
    <RadioGroupContext value={{ error, disabled, size }}>
      <div className="flex flex-col gap-(--spacing-8)">
        {label && (
          <span id={`${id}-label`} className={labelTextStyles()}>
            {label}
          </span>
        )}
        <RadioGroupPrimitive
          {...props}
          disabled={disabled}
          aria-labelledby={label ? `${id}-label` : props["aria-labelledby"]}
          aria-label={props["aria-label"]}
          aria-invalid={error || undefined}
          aria-disabled={disabled || undefined}
          className="flex flex-col gap-(--spacing-8)"
        >
          {children}
        </RadioGroupPrimitive>
      </div>
    </RadioGroupContext>
  );
}

// ============================================
// Radio
// ============================================

export type RadioProps = Omit<
  RadioPrimitive.Root.Props,
  "className" | "aria-label" | "aria-labelledby"
> & {
  description?: string;
} & (
    | { label: string; "aria-label"?: never }
    | { label?: never; "aria-label": string }
  );

/**
 * Individual radio button. Must be rendered inside a RadioGroup.
 *
 * @example
 * <Radio value="option-a" label="Option A" />
 * <Radio value="option-b" label="Option B" description="Secondary text" />
 */
function RadioComponent({
  label,
  description,
  disabled,
  ...props
}: RadioProps) {
  const { error, disabled: groupDisabled, size } = use(RadioGroupContext);
  const isDisabled = groupDisabled || disabled;
  const id = useId();
  const descriptionIds =
    [props["aria-describedby"], description ? `${id}-description` : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: Base UI renders the interactive radio control inside this label.
    <label className={radioItemVariants({ disabled: isDisabled })}>
      <div className="inline-flex items-center gap-(--spacing-8)">
        <RadioPrimitive.Root
          {...props}
          disabled={isDisabled}
          aria-label={label ? undefined : props["aria-label"]}
          aria-labelledby={label ? `${id}-label` : undefined}
          aria-describedby={descriptionIds}
          data-error={error ? "" : undefined}
          onKeyDown={(event) => {
            props.onKeyDown?.(event);
            if (!event.defaultPrevented && event.key === " ") {
              event.preventDefault();
              event.currentTarget.click();
            }
          }}
          className={cn(radioControlVariants({ size }))}
        >
          <span className={radioOutlineContainerVariants()}>
            <span className={radioOutlineIconVariants({ size })}>
              <RadioOutlineIcon />
            </span>
          </span>

          <RadioPrimitive.Indicator
            className={radioFilledIndicatorVariants({ size })}
          >
            <RadioDotIcon />
          </RadioPrimitive.Indicator>
        </RadioPrimitive.Root>

        {label && (
          <span
            id={`${id}-label`}
            className={cn(labelTextStyles(), isDisabled && "opacity-[0.54]")}
          >
            {label}
          </span>
        )}
      </div>

      {description && (
        <span
          id={`${id}-description`}
          className={cn(
            descriptionStyles(),
            isDisabled && "opacity-[0.54]",
            descriptionIndentVariants({ size }),
          )}
        >
          {description}
        </span>
      )}
    </label>
  );
}

export { RadioGroupComponent as RadioGroup, RadioComponent as Radio };
